import { getFirebase } from '../firebase';

// Helper to convert Firestore doc to regular object
const docToObj = (doc) => {
  const data = doc.data();
  return {
    ...data,
    id: doc.id,
    createdAt: data.createdAt?.toDate?.() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
    published_at: data.published_at?.toDate?.() || data.published_at
  };
};

// Helper for generating slugs
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Auth API calls
export const authAPI = {
  login: async ({ email, password }) => {
    try {
      const { auth, authLib: { signInWithEmailAndPassword } } = await getFirebase();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const token = await user.getIdToken();
      localStorage.setItem('token', token);
      const adminData = { email: user.email, id: user.uid, username: user.email.split('@')[0] };
      localStorage.setItem('admin', JSON.stringify(adminData));
      return { data: { success: true, token, admin: adminData } };
    } catch (error) {
      throw { response: { data: { message: error.message } } };
    }
  },
  logout: async () => {
    const { auth, authLib: { signOut } } = await getFirebase();
    await signOut(auth);
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
  },
  getMe: async () => {
    const { auth, authLib: { onAuthStateChanged } } = await getFirebase();
    return new Promise((resolve, reject) => {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          const adminData = { email: user.email, id: user.uid, username: user.email.split('@')[0] };
          resolve({ data: { success: true, admin: adminData } });
        } else {
          reject({ response: { status: 401, data: { success: false, message: 'Not authenticated' } } });
        }
      });
    });
  }
};

// Insights API calls
export const insightsAPI = {
  getAll: async (params = {}) => {
    const { db, firestore: { collection, query, orderBy, where, getDocs } } = await getFirebase();
    const insightsRef = collection(db, 'insights');
    
    // We fetch everything and filter in JS because Firestore doesn't support 
    // the complex OR/AND logic across multiple fields (status + published_at) easily 
    // without creating multiple indexes or composite queries that might be overkill for this scale.
    let q = query(insightsRef, orderBy('createdAt', 'desc'));

    if (params.category && params.category !== 'all') {
      q = query(insightsRef, where('category', '==', params.category), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    let insights = querySnapshot.docs.map(docToObj);
    
    const now = new Date();
    const status = params.status;

    // Filtering logic
    if (status === 'all') {
      // Admin view - return all
    } else if (status) {
      // Filter by specific status (from admin filters)
      insights = insights.filter(i => i.status === status);
      // For published/scheduled, also check date
      if (status === 'published' || status === 'scheduled') {
        insights = insights.filter(i => !i.published_at || new Date(i.published_at) <= now);
      }
    } else {
      // Public view - only show published/scheduled that have reached their time
      insights = insights.filter(i => 
        (i.status === 'published' || i.status === 'scheduled') && 
        (!i.published_at || new Date(i.published_at) <= now)
      );
    }

    if (params.limit) {
      const pageNum = params.page || 1;
      const limitNum = params.limit;
      const startIndex = (pageNum - 1) * limitNum;
      const total = insights.length;
      const totalPages = Math.ceil(total / limitNum);
      insights = insights.slice(startIndex, startIndex + limitNum);
      return { data: { success: true, insights, total, totalPages, page: pageNum } };
    }

    return { data: { success: true, insights, total: insights.length, totalPages: 1, page: 1 } };
  },
  getBySlug: async (slug) => {
    const { db, auth, firestore: { collection, query, where, limit, getDocs } } = await getFirebase();
    const q = query(collection(db, 'insights'), where('slug', '==', slug), limit(1));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw { response: { status: 404, data: { message: 'Insight not found' } } };
    }
    const insight = docToObj(querySnapshot.docs[0]);
    
    // Privacy check for non-admins
    if (!auth.currentUser) {
      const now = new Date();
      const isPublished = (insight.status === 'published' || insight.status === 'scheduled') && 
                         (!insight.published_at || new Date(insight.published_at) <= now);
      
      if (!isPublished || insight.status === 'draft') {
        throw { response: { status: 404, data: { message: 'Insight not found' } } };
      }
    }
    
    return { data: { success: true, ...insight } };
  },
  getById: async (id) => {
    const { db, firestore: { doc, getDoc } } = await getFirebase();
    const docRef = doc(db, 'insights', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw { response: { status: 404, data: { message: 'Insight not found' } } };
    }
    return { data: { success: true, ...docToObj(docSnap) } };
  },
  getRecent: async (count = 5) => {
    // Reuse getAll for consistency
    return insightsAPI.getAll({ limit: count });
  },
  create: async (data) => {
    const { db, firestore: { collection, addDoc, serverTimestamp } } = await getFirebase();
    const slug = generateSlug(data.title);
    const insightData = {
      ...data,
      slug,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, 'insights'), insightData);
    return { data: { success: true, ...insightData, id: docRef.id } };
  },
  update: async (id, data) => {
    const { db, firestore: { doc, updateDoc, serverTimestamp } } = await getFirebase();
    const docRef = doc(db, 'insights', id);
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    if (data.title) {
      updateData.slug = generateSlug(data.title);
    }
    await updateDoc(docRef, updateData);
    return { data: { success: true, message: 'Updated successfully' } };
  },
  delete: async (id) => {
    const { db, firestore: { doc, deleteDoc } } = await getFirebase();
    await deleteDoc(doc(db, 'insights', id));
    return { data: { success: true, message: 'Deleted successfully' } };
  }
};

// Contact API calls
export const contactAPI = {
  submit: async (data) => {
    const { db, firestore: { collection, addDoc, serverTimestamp } } = await getFirebase();
    const docRef = await addDoc(collection(db, 'contacts'), {
      ...data,
      status: 'new',
      createdAt: serverTimestamp()
    });
    return { data: { success: true, message: 'Message sent successfully', id: docRef.id } };
  },
  getAll: async () => {
    const { db, firestore: { collection, query, orderBy, getDocs } } = await getFirebase();
    const q = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const contacts = querySnapshot.docs.map(docToObj);
    return { data: { success: true, contacts } };
  },
  updateStatus: async (id, status) => {
    const { db, firestore: { doc, updateDoc, serverTimestamp } } = await getFirebase();
    const docRef = doc(db, 'contacts', id);
    await updateDoc(docRef, { status, updatedAt: serverTimestamp() });
    return { data: { success: true, message: 'Status updated' } };
  }
};

// Testimonial API calls
export const testimonialAPI = {
  getAll: async () => {
    const { db, firestore: { collection, query, orderBy, getDocs } } = await getFirebase();
    const q = query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const testimonials = querySnapshot.docs.map(docToObj);
    return { data: { success: true, testimonials } };
  },
  create: async (data) => {
    const { db, firestore: { collection, addDoc, serverTimestamp } } = await getFirebase();
    const testimonialData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, 'testimonials'), testimonialData);
    return { data: { success: true, ...testimonialData, id: docRef.id } };
  },
  update: async (id, data) => {
    const { db, firestore: { doc, updateDoc, serverTimestamp } } = await getFirebase();
    const docRef = doc(db, 'testimonials', id);
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    await updateDoc(docRef, updateData);
    return { data: { success: true, message: 'Updated successfully' } };
  },
  delete: async (id) => {
    const { db, firestore: { doc, deleteDoc } } = await getFirebase();
    await deleteDoc(doc(db, 'testimonials', id));
    return { data: { success: true, message: 'Deleted successfully' } };
  }
};

// FAQ API calls
export const faqAPI = {
  getAll: async () => {
    const { db, firestore: { collection, query, orderBy, getDocs } } = await getFirebase();
    const q = query(collection(db, 'faqs'), orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    const faqs = querySnapshot.docs.map(docToObj);
    return { data: { success: true, faqs } };
  },
  create: async (data) => {
    const { db, firestore: { collection, addDoc, serverTimestamp } } = await getFirebase();
    const faqData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, 'faqs'), faqData);
    return { data: { success: true, ...faqData, id: docRef.id } };
  },
  update: async (id, data) => {
    const { db, firestore: { doc, updateDoc, serverTimestamp } } = await getFirebase();
    const docRef = doc(db, 'faqs', id);
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    await updateDoc(docRef, updateData);
    return { data: { success: true, message: 'Updated successfully' } };
  },
  delete: async (id) => {
    const { db, firestore: { doc, deleteDoc } } = await getFirebase();
    await deleteDoc(doc(db, 'faqs', id));
    return { data: { success: true, message: 'Deleted successfully' } };
  }
};

// Upload API calls
export const uploadAPI = {
  uploadImage: async (formData) => {
    const file = formData.get('image');
    if (!file) throw new Error('No file found');

    const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
    const fileName = `${Date.now()}-${sanitizedName}`;
    
    const { auth, storage, storageLib: { ref, uploadBytes, getDownloadURL } } = await getFirebase();
    const storageRef = ref(storage, `insights/${fileName}`);
    
    console.log(`Starting upload: Name=${fileName}, Size=${(file.size / 1024).toFixed(2)}KB`);
    if (!auth.currentUser) {
      console.warn('Warning: Not authenticated. Upload may fail.');
    }

    const uploadPromise = uploadBytes(storageRef, file);

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Upload timeout after 120s. This is almost always a CORS issue or a blocked network connection.')), 120000)
    );

    try {
      const snapshot = await Promise.race([uploadPromise, timeoutPromise]);
      console.log('Upload successful, getting download URL...');
      const downloadURL = await getDownloadURL(snapshot.ref);
      return { data: { success: true, url: downloadURL, fileUrl: downloadURL } };
    } catch (error) {
      console.error('Detailed Upload Error:', error);
      console.error('Error Code:', error.code);
      console.error('Error Message:', error.message);
      
      const isCorsError = error.message?.toLowerCase().includes('cors') || 
                          error.code === 'storage/retry-limit-exceeded' || 
                          !error.code; 

      if (isCorsError) {
        throw new Error('Upload blocked by CORS. Please ensure the Cloud Shell commands I gave you finished with a "Success" message.');
      }
      throw error;
    }
  },
  deleteImage: async (imagePath) => {
    if (imagePath.includes('firebasestorage.googleapis.com')) {
      const { storage, storageLib: { ref, deleteObject } } = await getFirebase();
      const storageRef = ref(storage, imagePath);
      await deleteObject(storageRef);
    }
    return { data: { success: true, message: 'Deleted' } };
  }
};

export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/placeholder.jpg';
  return imageUrl; 
};

const api = {
  interceptors: {
    request: { use: () => { } },
    response: { use: () => { } }
  }
};

export default api;